import qs from "qs";

import { qsSettings } from "lib/hooks/useURLState";

/**
 * Splits a URL (absolute or relative) into a TanStack Router `to` path and a
 * parsed `search` object, so internal links can navigate within the SPA instead
 * of doing a full page reload.
 *
 * Parses the query with the same `qsSettings` the router uses, so the resulting
 * `search` round-trips to the same value the destination route would read from
 * the URL.
 */
export function toAndSearch(url: string): { to: string; search?: Record<string, unknown> } {
  let pathname: string;
  let query: string;
  try {
    const parsed = new URL(url, window.location.origin);
    pathname = parsed.pathname;
    query = parsed.search.replace(/^\?/, "");
  } catch {
    [pathname, query = ""] = url.split("?");
  }

  return query
    ? { to: pathname, search: qs.parse(query, qsSettings) as Record<string, unknown> }
    : { to: pathname };
}

/**
 * Inverse of {@link toAndSearch}: serialises a TanStack `{ to, search }` target back into a URL
 * string, for the few places that genuinely need a string — a raw `href`, a `target="_blank"`
 * link, or the report/print views that render without a `RouterProvider`. Lets the structured
 * `{ to, search }` form be canonical and the URL string be the derived exception.
 */
export function targetToUrl({
  to,
  search,
}: {
  to: string;
  search?: Record<string, unknown>;
}): string {
  if (!search) return to;
  return `${to}?${qs.stringify(search, { arrayFormat: "brackets" })}`;
}
